import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Save, Eye, EyeOff, Plus, X, Search, LayoutGrid } from "lucide-react";
import { toast } from "sonner";

export default function ConfigHomeShelves() {
  const [rows, setRows] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // slug currently editing game_ids

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: shelves }, { data: games }] = await Promise.all([
        api.get("/settings/home-shelves"),
        api.get("/games"),
      ]);
      setRows(shelves);
      setAllGames(games.filter((g) => g.status === "active"));
    } catch (_) {
      /* interceptor */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (slug, patch) => {
    setRows((prev) => prev.map((r) => (r.slug === slug ? { ...r, ...patch, _dirty: true } : r)));
  };

  const save = async (row) => {
    setSaving(row.slug);
    try {
      await api.patch(`/settings/home-shelves/${row.slug}`, {
        title: row.title,
        enabled: row.enabled,
        more_to: row.more_to,
        order: Number(row.order) || 0,
        game_ids: row.game_ids || [],
      });
      toast.success(`"${row.title}" shelf saved`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const toggleEnabled = async (row) => {
    const newVal = !row.enabled;
    update(row.slug, { enabled: newVal });
    try {
      await api.patch(`/settings/home-shelves/${row.slug}`, { enabled: newVal });
      toast.success(`Shelf ${newVal ? "enabled" : "disabled"}`);
    } catch (_) {
      update(row.slug, { enabled: !newVal });
      toast.error("Toggle failed");
    }
  };

  return (
    <div data-testid="home-shelves-page">
      <PageHeader
        icon={LayoutGrid}
        title="Home Shelves"
        description="Curate the carousels on the public homepage. Pick specific games for each shelf, rename them, toggle visibility, or reorder."
      />

      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm px-4 py-3 mb-5 text-xs text-emerald-300">
        <b>How it works:</b> Click <b>"Add Games"</b> on a shelf to hand-pick which games appear on the public homepage carousel. Admin selection overrides the legacy type-filter fallback. Changes go live within seconds.
      </div>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}

      <div className="space-y-3">
        {rows.map((r) => {
          const selected = r.game_ids || [];
          const selectedGames = selected
            .map((id) => allGames.find((g) => g.id === id))
            .filter(Boolean);
          return (
            <div
              key={r.slug}
              className="bg-[#111a26] border border-[#1f2a3a] rounded-lg p-4"
              data-testid={`shelf-row-${r.slug}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <button
                  onClick={() => toggleEnabled(r)}
                  className={`shrink-0 h-9 w-9 rounded flex items-center justify-center border ${
                    r.enabled
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                      : "bg-zinc-500/10 border-zinc-500/30 text-zinc-500"
                  }`}
                  title={r.enabled ? "Visible on homepage" : "Hidden"}
                  data-testid={`shelf-toggle-${r.slug}`}
                >
                  {r.enabled ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>

                <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-3 items-start min-w-0">
                  <div className="lg:col-span-4">
                    <Label>Title (shown on homepage)</Label>
                    <input
                      type="text"
                      value={r.title || ""}
                      onChange={(e) => update(r.slug, { title: e.target.value })}
                      className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm focus:outline-none focus:border-emerald-500"
                      data-testid={`shelf-title-${r.slug}`}
                    />
                    <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                      slug: {r.slug}
                    </div>
                  </div>

                  <div className="lg:col-span-3">
                    <Label>"More" link</Label>
                    <input
                      type="text"
                      value={r.more_to || ""}
                      onChange={(e) => update(r.slug, { more_to: e.target.value })}
                      placeholder="/casino"
                      className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm focus:outline-none focus:border-emerald-500 font-mono"
                      data-testid={`shelf-more-${r.slug}`}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <Label>Order</Label>
                    <input
                      type="number"
                      value={r.order ?? 0}
                      onChange={(e) => update(r.slug, { order: e.target.value })}
                      className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm focus:outline-none focus:border-emerald-500"
                      data-testid={`shelf-order-${r.slug}`}
                    />
                  </div>

                  <div className="lg:col-span-3 flex items-end h-full pt-[22px]">
                    <button
                      onClick={() => save(r)}
                      disabled={saving === r.slug}
                      className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm font-semibold rounded inline-flex items-center justify-center gap-1.5"
                      data-testid={`shelf-save-${r.slug}`}
                    >
                      <Save size={14} /> {saving === r.slug ? "Saving…" : "Save shelf"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Curated games row */}
              <div className="mt-4 pt-4 border-t border-[#1f2a3a]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">
                    Games in this shelf
                    <span className="ml-2 text-zinc-600 normal-case">
                      ({selectedGames.length} curated{selectedGames.length === 0 ? " — falling back to type filter" : ""})
                    </span>
                  </div>
                  <button
                    onClick={() => setPickerFor(r.slug)}
                    data-testid={`shelf-pick-games-${r.slug}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded transition-colors"
                  >
                    <Plus size={14} /> Add / Edit Games
                  </button>
                </div>
                {selectedGames.length === 0 ? (
                  <div className="text-sm text-zinc-500 py-4 text-center bg-[#0a1119] rounded border border-dashed border-[#27272A]">
                    No games curated yet. Click <b className="text-zinc-300">Add / Edit Games</b> to pick.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedGames.map((g) => (
                      <div
                        key={g.id}
                        className="group flex items-center gap-2 px-2 py-1 bg-[#0a1119] border border-[#27272A] rounded text-xs"
                      >
                        {g.image ? (
                          <img src={g.image} alt="" className="h-6 w-6 rounded object-cover" />
                        ) : (
                          <div className="h-6 w-6 rounded bg-[#1f2a3a]" />
                        )}
                        <span className="text-zinc-200 font-medium">{g.name}</span>
                        <span className="text-zinc-600 text-[10px]">{g.provider}</span>
                        <button
                          onClick={() =>
                            update(r.slug, {
                              game_ids: selected.filter((id) => id !== g.id),
                            })
                          }
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-red-400 ml-1"
                          title="Remove from shelf"
                          data-testid={`shelf-${r.slug}-remove-${g.id}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pickerFor && (
        <GamePickerModal
          shelf={rows.find((r) => r.slug === pickerFor)}
          allGames={allGames}
          onClose={() => setPickerFor(null)}
          onApply={(ids) => {
            update(pickerFor, { game_ids: ids });
            setPickerFor(null);
          }}
        />
      )}
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
      {children}
    </label>
  );
}

function GamePickerModal({ shelf, allGames, onClose, onApply }) {
  const [draft, setDraft] = useState(shelf?.game_ids || []);
  const [search, setSearch] = useState("");

  const toggle = (id) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allGames;
    return allGames.filter(
      (g) =>
        (g.name || "").toLowerCase().includes(q) ||
        (g.provider || "").toLowerCase().includes(q) ||
        (g.type || "").toLowerCase().includes(q) ||
        (g.game_uid || "").toLowerCase().includes(q)
    );
  }, [allGames, search]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="shelf-picker-modal"
    >
      <div
        className="w-full max-w-3xl rounded-xl flex flex-col max-h-[85vh] bg-[#111a26] border border-[#1f2a3a]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f2a3a]">
          <div>
            <h3 className="text-lg font-bold text-white">Pick games for "{shelf?.title}"</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              {draft.length} selected · drag order follows pick order · works with all providers (JILI, Evolution, PG Soft, etc.)
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/5">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-[#1f2a3a]">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, provider, type, or game_uid…"
              data-testid="shelf-picker-search"
              className="w-full h-10 pl-9 pr-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm focus:outline-none focus:border-emerald-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {visible.length === 0 && (
            <div className="py-16 text-center text-zinc-500 text-sm">
              {allGames.length === 0
                ? "No active games in catalog. Add games from /root/admin/bets/games first."
                : "No games match your search."}
            </div>
          )}
          {visible.map((g) => {
            const on = draft.includes(g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => toggle(g.id)}
                data-testid={`shelf-picker-game-${g.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-colors text-left ${
                  on ? "bg-emerald-500/10 border border-emerald-500/30" : "hover:bg-white/5 border border-transparent"
                }`}
              >
                <span
                  className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    on ? "bg-emerald-500 border-emerald-500" : "border-white/25 bg-transparent"
                  }`}
                >
                  {on && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.2 5 8.7l4.5-5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {g.image ? (
                  <img src={g.image} alt="" className="h-10 w-10 rounded object-cover" />
                ) : (
                  <div className="h-10 w-10 rounded bg-[#1f2a3a] flex items-center justify-center text-zinc-600 text-[10px] font-mono">
                    {g.name?.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-semibold truncate">{g.name}</div>
                  <div className="text-[11px] text-zinc-500 flex gap-2">
                    <span>{g.provider}</span>
                    <span>·</span>
                    <span>{g.type}</span>
                    {g.game_uid && (
                      <>
                        <span>·</span>
                        <span className="font-mono">uid:{g.game_uid}</span>
                      </>
                    )}
                  </div>
                </div>
                {on && (
                  <span className="text-[10px] font-mono text-emerald-400">
                    #{draft.indexOf(g.id) + 1}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-[#1f2a3a]">
          <button
            onClick={() => setDraft([])}
            data-testid="shelf-picker-clear"
            className="text-xs text-zinc-500 hover:text-white"
          >
            Clear all
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-[#27272A] rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => onApply(draft)}
              data-testid="shelf-picker-apply"
              className="px-4 py-2 text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded"
            >
              Apply ({draft.length}) — then click Save shelf
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
